import Alert from '@storeblocks/alert';
import { Button } from '@storeblocks/button';
import Modal from '@storeblocks/modal';
import { type FC, useEffect } from 'react';

import { OrganizationUser } from '@/api/organizationUsers/models/OrganizationUser';
import {
  useDeleteOrganizationUserMutation,
  useOrganizationUsersQuery,
} from '@/api/organizationUsers/organizationUsersApi';
import { User } from '@/api/users/models/User';
import { useDeleteUserMutation } from '@/api/users/usersApi';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { TrackingElement, useGetTrackId } from '@/tracking';

interface PropsModal {
  show: boolean;
  name: string;
  isError: boolean;
  onClose: () => void;
  onDelete: () => void;
}

const DeleteModal: FC<PropsModal> = ({
  show,
  name,
  isError,
  onClose,
  onDelete,
}) => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  return (
    <Modal
      trigger={undefined}
      open={show}
      onClose={onClose}
      title={`${texts.admin.delete.areYouSure} ${name}?`}
      content={
        <>
          {texts.admin.delete.cannotBeReverted}
          {isError && (
            <Alert
              fluid
              variant="error"
              description={texts.admin.user.deleteError}
              data-test="add-organization-error-alert"
            />
          )}
        </>
      }
      footer={
        <WithGap gap="16" direction="row">
          <Button
            onClick={onDelete}
            key="delete-button"
            data-trackid={getTrackId(
              'delete-user:confirm-delete-user-modal',
              TrackingElement.Button,
            )}
          >
            {texts.admin.user.delete}
          </Button>
          <Button
            variant="subtle"
            onClick={onClose}
            key="cancel-button"
            data-trackid={getTrackId(
              'cancel:confirm-delete-user-modal',
              TrackingElement.Button,
            )}
          >
            {texts.admin.user.cancel}
          </Button>
        </WithGap>
      }
    ></Modal>
  );
};

interface PropsIndividualUser {
  show: boolean;
  user: User;
  onClose: () => void;
}

export const DeleteIndividualUserModal: FC<PropsIndividualUser> = ({
  show,
  user,
  onClose,
}) => {
  const [deleteUser, deleteUserRequest] = useDeleteUserMutation();

  const handleDelete = (): void => {
    deleteUser(user.userId);
  };

  useEffect(() => {
    if (deleteUserRequest.isSuccess) {
      onClose();
    }
  }, [deleteUserRequest.isSuccess]);

  return (
    <DeleteModal
      show={show}
      name={`${user.givenName} ${user.familyName}`}
      isError={deleteUserRequest.isError}
      onDelete={handleDelete}
      onClose={onClose}
    />
  );
};

interface PropsOrganizationUser {
  show: boolean;
  user: OrganizationUser;
  onClose: () => void;
}

export const DeleteOrganizationUserModal: FC<PropsOrganizationUser> = ({
  show,
  user,
  onClose,
}) => {
  const [deleteOrganizationUser, deleteOrganizationUserRequest] =
    useDeleteOrganizationUserMutation();

  const organizationUsersQuery = useOrganizationUsersQuery();

  const handleDelete = (): void => {
    deleteOrganizationUser(user.userId);
  };

  useEffect(() => {
    if (deleteOrganizationUserRequest.isSuccess) {
      organizationUsersQuery.refetch();
      onClose();
    }
  }, [deleteOrganizationUserRequest.isSuccess]);

  return (
    <DeleteModal
      show={show}
      name={`${user.givenName} ${user.familyName}`}
      isError={deleteOrganizationUserRequest.isError}
      onDelete={handleDelete}
      onClose={onClose}
    />
  );
};
